import { type ReactNode } from 'react';
import { COUNTRIES } from '@/utils/countries';
import { FieldInput, FieldSelect } from '@/components/ui/field-input';
import { splitPhone, joinPhone } from '@/utils/phone';
import { ProfileSection, ProfileField } from './profile-section';
import type { ProfileUpdate } from '../types';

type Props = {
  values: ProfileUpdate;
  setField: <K extends keyof ProfileUpdate>(k: K, v: ProfileUpdate[K]) => void;
};

function CountrySelect({ values, setField }: Props): ReactNode {
  return (
    <FieldSelect
      value={values.country ?? ''}
      onChange={(e) => setField('country', e.target.value)}
    >
      <option value="">Seleccionar país</option>
      {COUNTRIES.map((c) => (
        <option key={c.code} value={c.code}>
          {c.name}
        </option>
      ))}
    </FieldSelect>
  );
}

function PrefixSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): ReactNode {
  return (
    <FieldSelect value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Prefijo</option>
      {COUNTRIES.map((c) => (
        <option key={c.code} value={c.dialCode}>
          {c.dialCode} ({c.code})
        </option>
      ))}
    </FieldSelect>
  );
}

function PhoneRow({ values, setField }: Props): ReactNode {
  const { prefix, number } = splitPhone(values.phone ?? '');
  const update = (p: string, n: string): void =>
    setField('phone', joinPhone({ prefix: p, number: n }));
  return (
    <div className="grid grid-cols-1 gap-sm sm:grid-cols-[120px_1fr]">
      <PrefixSelect value={prefix} onChange={(v) => update(v, number)} />
      <FieldInput
        type="tel"
        value={number}
        onChange={(e) => update(prefix, e.target.value)}
        placeholder="600 000 000"
      />
    </div>
  );
}

export function ContactSection(p: Props): ReactNode {
  return (
    <ProfileSection title="Contacto y ubicación">
      <ProfileField label="Teléfono">
        <PhoneRow {...p} />
      </ProfileField>
      <div className="grid gap-base sm:grid-cols-2">
        <ProfileField label="País">
          <CountrySelect {...p} />
        </ProfileField>
        <ProfileField label="Ciudad">
          <FieldInput
            value={p.values.city ?? ''}
            onChange={(e) => p.setField('city', e.target.value)}
            placeholder="Madrid"
          />
        </ProfileField>
      </div>
    </ProfileSection>
  );
}
